<template>
	<template v-if="enroller && allowReselect">
		<div class="flex flex-col border px-3 py-2 border-gray-500">
			<div class="flex justify-start">
				<h3 class="h3 seint-h3 m-0">{{ enroller.firstName }} {{ enroller.lastName }}</h3>
			</div>
			<div class="flex gap-3">
				<span class="flex items-center gap-1 seint-note text-seint-note">
					<svg-vue icon="marker-pin"></svg-vue>{{ enroller.mainCity }}, {{ enroller.mainState }}
				</span>
				<span class="text-seint-note">ID:{{ enroller.customerId }}</span>
			</div>
		</div>
		<div class="w-full flex justify-center !pt-5">
			<button type="button" class="underline text-seint-placeholder" @click="handleEnrollerClear">
				Select a different Artist
			</button>
		</div>
	</template>
	<div v-else class="!pb-4">
		<div class="relative h-14">
			<svg-vue icon="search-icon" class="absolute top-2/4 left-3 -translate-y-2/4 z-10 w-4" />
			<input
				id="artist-search"
				class="absolute inset-0 w-full !h-full !py-3 !px-9 bg-white border border-gray-300 focus:!outline-none focus:border focus:!border-black focus:!ring-0 autofill:shadow-fill-white"
				type="search"
				placeholder="Search by Artist name or ID"
				v-model="query"
				@input="refineSearch()"
			/>
			<label class="hidden" for="artist-search">Search by Artist name or ID</label>
			<button
				v-if="query.length > 0"
				type="button"
				@click="handleSearchClear()"
				aria-label="Clear Search"
				class="absolute top-2/4 right-3 -translate-y-2/4 z-10 w-4"
			>
				<svg-vue icon="close" />
			</button>
		</div>
		<div v-show="results.length > 0" class="w-full">
			<ul class="p-0 overflow-y-scroll max-h-64 border border-b border-gray-500">
				<li
					v-for="artist in results"
					:key="artist.customerId"
					class="flex justify-between border border-t-0 px-3 py-2 border-gray-500"
				>
					<div class="flex flex-col">
						<div class="flex justify-start">
							<h4 class="h4 text-sm font-medium m-0">{{ artist.firstName }} {{ artist.lastName }}</h4>
						</div>
						<div class="flex gap-3">
							<span class="flex items-center gap-1 seint-note text-seint-note"
								><svg-vue icon="marker-pin" />{{ artist.city }}, {{ artist.state }}</span
							>
							<span class="text-seint-note">ID:{{ artist.customerId }}</span>
						</div>
					</div>
					<button
						v-if="isEnrollment"
						type="button"
						class="underline flex items-start h-full text-sm font-normal"
						aria-label="Select"
						@click="() => handleSelect(artist)"
					>
						Select
					</button>
					<div v-else class="flex gap-3">
						<a
							v-if="artist.instagram"
							:href="`https://www.instagram.com/${artist.instagram}`"
							target="_blank"
							rel="noopener noreferrer"
							class="flex flex-col items-center"
							aria-label="Visit Artist Instagram"
						>
							<span class="bg-black w-9 h-9 grid place-items-center rounded-full text-white">
								<svg-vue icon="instagram" class="w-5" />
							</span>
							<span class="text-xs">&ensp;View ↗</span>
						</a>
						<button
							type="button"
							@click="() => handleSelect(artist)"
							class="flex flex-col items-center"
							aria-label="Select Artist"
						>
							<span class="bg-black w-9 h-9 grid place-items-center rounded-full text-white">
								<svg-vue icon="bag-v2" class="w-5 fill-white" />
							</span>
							<span class="text-xs">Shop</span>
						</button>
					</div>
				</li>
			</ul>
		</div>
	</div>
	<form v-if="isEnrollment" method="POST" :action="endpoint" ref="artistSearchForm">
		<slot name="csrf" />
		<input type="hidden" name="enroller_id" v-model="enrollerId" />
		<slot name="actions" />
	</form>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import axios from 'axios'

const SEARCH_ENDPOINT = import.meta.env.VITE_SEARCH_ENDPOINT
const SEARCH_TOKEN = import.meta.env.VITE_MEILISEARCH_CLIENT_KEY
const SEARCH_INDEX = 'artist-search'

const props = defineProps({
	isEnrollment: {
		type: Boolean,
		defualt: false,
	},
	endpoint: {
		type: String,
		default: null,
	},
	redirectOnSelect: {
		type: Boolean,
		default: false,
	},
	postOnSelect: {
		type: Boolean,
		default: false,
	},
	allowReselect: {
		type: Boolean,
		default: false,
	},
	initialEnroller: {
		type: Object,
		required: false,
		default: null,
	},
	buttonId: {
		type: String,
		required: false,
		default: null,
	},
	affiliateClearEndpoint: {
		type: String,
		default: null,
	},
})

const emit = defineEmits(['selected'])

let searchClient
let debounce
const enroller = ref(props.initialEnroller)
const query = ref('')
const limit = 50
const results = ref([])
const artistSearchForm = ref(null)
const enrollerId = ref(props.initialEnroller?.customerId)

onMounted(() => {
	const config = {
		baseURL: `${SEARCH_ENDPOINT}/indexes`,
		headers: {
			Authorization: `Bearer ${SEARCH_TOKEN}`,
			'Content-Type': 'application/json',
			Accepts: 'application/json',
		},
	}
	searchClient = axios.create(config)

	if (props.allowReselect && enroller.value && props.buttonId) {
		document.getElementById(props.buttonId).disabled = false
	}
})

const refineSearch = () => {
	clearTimeout(debounce)
	debounce = setTimeout(async () => {
		try {
			if (query.value.trim().length === 0) {
				results.value = []
				return
			}
			const url = `${SEARCH_INDEX}/search`
			const { data } = await searchClient.get(url, { params: { q: query.value, limit, filter: `customerStatus=1` } })
			results.value = data?.hits ?? []
		} catch (error) {
			console.error(error.response)
		}
	}, 250)
}

const handleSearchClear = () => {
	query.value = ''
	results.value = []
}

const handleSelect = async (artist) => {
	if (props.redirectOnSelect) {
		const { protocol, href, hostname } = window.location
		const [subdomain] = hostname.split('.')
		const url = href.replace(subdomain, '').replace(protocol, '').replace('//', '')
		if (props.affiliateClearEndpoint) {
			await axios.delete(props.affiliateClearEndpoint)
		}
		window.location.href = `${protocol}//${artist.webAlias}${url}`
		return
	}
	enrollerId.value = artist.customerId
	enroller.value = { ...artist, mainCity: artist.city, mainState: artist.state }
	if (props.postOnSelect) {
		nextTick(() => artistSearchForm.value.submit())
	}
	if (props.buttonId) {
		document.getElementById(props.buttonId).disabled = false
	}
	emit('selected', artist)
}

const handleEnrollerClear = () => {
	enroller.value = null
	if (props.buttonId) {
		document.getElementById(props.buttonId).disabled = true
	}
}
</script>
<style scoped lang="scss">
input[type='search'] {
	/* clears the ‘X’ from Internet Explorer */
	&::-ms-clear {
		display: none;
		width: 0;
		height: 0;
	}
	&::-ms-reveal {
		display: none;
		width: 0;
		height: 0;
	}
	/* clears the ‘X’ from Chrome */
	&::-webkit-search-decoration,
	&::-webkit-search-cancel-button,
	&::-webkit-search-results-button,
	&::-webkit-search-results-decoration {
		display: none;
	}
}
</style>
